// M040 C2: hardcoded colors → tokens, text-* → text-tok-*
/**
 * DecisionFlow — RateTableBuilder
 * data-fit LLC · Confidential
 * Stage 3: DATA SETUP — Screen SETUP-05
 * Spec authority: ComponentSpec_DATASETUP_v1_corrected.md (Session 90)
 * MasterContext authority: MasterContext_Session90_Close.md
 * Generated: Session 90 — ChatGPT Phase 3
 * Patched: Session 91 — PATCH-04 (ceiling pct DDL constraints validated client-side)
 *                      PATCH-05 (interpolation changed to select per DDL CHECK 'linear'|'step')
 * Patched: S205 — CSS fix: df-* classes replaced with Tailwind utilities (no new CSS files)
 * Path: ui/src/components/tenant-admin/RateTableBuilder.jsx
 *
 * OPEN ITEMS IN THIS FILE:
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { API_BASE } from './DataSetupFlowShell';

const FLOAT_TOLERANCE = 1e-4;

const EMPTY_HEADER_FORM = {
  role_id: '',
  segment_id: '',
  business_unit_id: '',
  rate_table_code: '',
  name: '',
  default_ceiling_pct: '',
  attainment_ceiling_pct: '',
  plan_component_id: ''
};

const EMPTY_ROW_FORM = {
  band_sequence: '',
  from_pct: '',
  to_pct: '',
  payout_pct: '',
  is_accelerator: false,
  interpolation: 'linear'  // DDL default: 'linear'
};

function toIntegerOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getErrorMessage(error, fallback) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function validateBandContiguity(rows) {
  const sortedRows = [...rows].sort(
    (left, right) => Number(left.band_sequence) - Number(right.band_sequence)
  );

  for (const row of sortedRows) {
    const fromPct = Number(row.from_pct);
    const toPct = Number(row.to_pct);

    if (!Number.isFinite(fromPct) || !Number.isFinite(toPct)) {
      return 'Each band must have numeric from_pct and to_pct values.';
    }

    if (fromPct >= toPct) {
      return `Band ${row.band_sequence} must have from_pct less than to_pct.`;
    }
  }

  for (let index = 1; index < sortedRows.length; index += 1) {
    const previous = sortedRows[index - 1];
    const current = sortedRows[index];

    const previousTo = Number(previous.to_pct);
    const currentFrom = Number(current.from_pct);

    if (currentFrom > previousTo + FLOAT_TOLERANCE) {
      return `Gap detected between band ${previous.band_sequence} and band ${current.band_sequence}.`;
    }

    if (currentFrom < previousTo - FLOAT_TOLERANCE) {
      return `Overlap detected between band ${previous.band_sequence} and band ${current.band_sequence}.`;
    }
  }

  return '';
}

function RateTableBuilder({ tier = 'essentials', onBack, onNext }) {
  const { user } = useAuth();

  const [rateTables, setRateTables] = useState([]);
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [segments, setSegments] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [planComponents, setPlanComponents] = useState([]);
  // fiscalPeriods state removed (B3 fix): plan_component.effective_date is the
  // authoritative source per r38 DDL — plan_component has no fiscal_period_id column.
  const [headerForm, setHeaderForm] = useState(EMPTY_HEADER_FORM);
  const [rowForm, setRowForm] = useState(EMPTY_ROW_FORM);
  const [selectedRateTableId, setSelectedRateTableId] = useState(null);
  const [editingRows, setEditingRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [blockingError, setBlockingError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [nonBlockingWarning, setNonBlockingWarning] = useState('');
  const [toast, setToast] = useState(null);

  // ── S471 Unified-path import state (POST /api/v1/import/upload) ──────────
  // Shells: file_type=rate_table_shell — creates unapproved rate_table rows
  // Rows:   file_type=rate_table_row   — loads bands into approved rate tables
  const [csvShellsFile, setCsvShellsFile]             = useState(null);
  const [csvRowsFile, setCsvRowsFile]                 = useState(null);
  const [csvShellsDryResult, setCsvShellsDryResult]   = useState(null);
  const [csvRowsDryResult, setCsvRowsDryResult]       = useState(null);
  const [csvShellsCommitted, setCsvShellsCommitted]   = useState(false);
  const [csvRowsCommitted, setCsvRowsCommitted]       = useState(false);
  const [csvUnifiedImporting, setCsvUnifiedImporting] = useState(false);
  const [csvUnifiedError, setCsvUnifiedError]         = useState('');

  const hasBusinessUnitScope = tier === 'standard' || tier === 'enterprise';

  const fetchJson = useCallback(
    async (url, options = {}) => {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          ...(options.headers || {})
        }
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Request failed with status ${response.status}`);
      }

      if (response.status === 204) return null;
      return response.json();
    },
    []
  );

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const loadRateTables = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setInlineError('');

    try {
      const data = await fetchJson(`${API_BASE}/rate-tables`);
      setRateTables(Array.isArray(data) ? data : data?.rate_tables || data?.data || []);
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to fetch rate tables.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, user]);

  const loadPlanComponents = useCallback(async () => {
    if (!user) return;

    try {
      const data = await fetchJson(`${API_BASE}/plan-components`);
      setPlanComponents(
        Array.isArray(data) ? data : data?.plan_components || data?.data || []
      );
    } catch (error) {
      setInlineError(
        getErrorMessage(error, 'Plan component endpoint unavailable or unconfirmed.')
      );
    }
  }, [fetchJson, user]);

  const loadReferenceData = useCallback(async () => {
    if (!user) return;

    setReferenceLoading(true);
    setBlockingError('');

    try {
      // B3 fix: fiscal-periods fetch removed — effective_date now resolved directly
      // from plan_component.effective_date (r38 DDL: plan_component has no fiscal_period_id column).
      const [rolesData, segmentsData, businessUnitsData] = await Promise.all([
        fetchJson(`${API_BASE}/roles`),
        fetchJson(`${API_BASE}/segments`),
        hasBusinessUnitScope
          ? fetchJson(`${API_BASE}/business-units`)
          : Promise.resolve([]),
      ]);

      setRoles(Array.isArray(rolesData) ? rolesData : rolesData?.roles || rolesData?.data || []);
      setSegments(
        Array.isArray(segmentsData) ? segmentsData : segmentsData?.segments || segmentsData?.data || []
      );
      setBusinessUnits(
        Array.isArray(businessUnitsData)
          ? businessUnitsData
          : businessUnitsData?.business_units || businessUnitsData?.data || []
      );
    } catch (error) {
      setBlockingError(
        getErrorMessage(error, 'Unable to fetch required rate table reference data.')
      );
    } finally {
      setReferenceLoading(false);
    }
  }, [fetchJson, hasBusinessUnitScope, user]);

  // GET /rate-table-rows does not exist — rows returned from POST /rate-tables/{id}/rows
  // and stored in local state. Selecting a new table clears rows.
  const loadRowsForTable = useCallback((rateTableId) => {
    if (!rateTableId) setRows([]);
  }, []);

  useEffect(() => { loadReferenceData(); }, [loadReferenceData]);
  useEffect(() => { loadRateTables(); }, [loadRateTables]);
  useEffect(() => { loadPlanComponents(); }, [loadPlanComponents]);
  useEffect(() => { loadRowsForTable(selectedRateTableId); }, [loadRowsForTable, selectedRateTableId]);

  useEffect(() => {
    const hasUnapproved = rateTables.some((table) => !table.approved_by);
    setNonBlockingWarning(
      hasUnapproved ? 'Unapproved rate tables are engine-excluded until approved.' : ''
    );
  }, [rateTables]);

  const updateHeaderForm = useCallback((field, value) => {
    setHeaderForm((previous) => ({ ...previous, [field]: value }));
  }, []);

  const updateRowForm = useCallback((field, value) => {
    setRowForm((previous) => {
      const next = { ...previous, [field]: value };
      if (field === 'from_pct') {
        const fromPct = Number(value);
        next.is_accelerator = Number.isFinite(fromPct) && fromPct >= 1.0;
      }
      return next;
    });
  }, []);

  const validateHeader = useCallback(() => {
    const errors = {};
    if (!headerForm.role_id) errors.role_id = 'Role is required.';
    if (!headerForm.segment_id) errors.segment_id = 'Segment is required.';
    if (!headerForm.rate_table_code.trim()) errors.rate_table_code = 'Rate table code is required.';
    if (!headerForm.name.trim()) errors.name = 'Name is required.';
    if (!headerForm.plan_component_id) errors.plan_component_id = 'Plan component is required.';

    const defaultCeiling = toNumberOrNull(headerForm.default_ceiling_pct);
    if (defaultCeiling !== null && (defaultCeiling < 0.85 || defaultCeiling > 1.0)) {
      errors.default_ceiling_pct = 'Default ceiling must be between 0.85 and 1.0.';
    }

    const attainmentCeiling = toNumberOrNull(headerForm.attainment_ceiling_pct);
    if (attainmentCeiling !== null && attainmentCeiling <= 1.0) {
      errors.attainment_ceiling_pct = 'Attainment ceiling must be greater than 1.0.';
    }

    return errors;
  }, [headerForm]);

  const validateRow = useCallback((values) => {
    const errors = {};
    if (toIntegerOrNull(values.band_sequence) === null) errors.band_sequence = 'Band sequence is required.';
    if (toNumberOrNull(values.from_pct) === null) errors.from_pct = 'from_pct is required.';
    if (toNumberOrNull(values.to_pct) === null) errors.to_pct = 'to_pct is required.';
    if (toNumberOrNull(values.payout_pct) === null) errors.payout_pct = 'payout_pct is required.';
    return errors;
  }, []);

  const buildHeaderPayload = useCallback(() => {
    // B3 fix (OI-S383-06 / OI-S206-09):
    // plan_component has no fiscal_period_id column in r38 DDL — the prior lookup chain
    // (plan_component.fiscal_period_id → fiscalPeriods list → period_start_date) always
    // resolved to null, silently falling back to CURRENT_DATE at the API layer.
    // Fix: read effective_date directly from plan_component (date NN, set to period start
    // when the plan was created). This satisfies the engine temporal check:
    //   effective_date <= period_start_date.
    const selectedComponent = planComponents.find(
      (c) => String(c.plan_component_id) === String(headerForm.plan_component_id)
    );
    const effectiveDate = selectedComponent?.effective_date ?? null;

    return {
      role_id: toIntegerOrNull(headerForm.role_id),
      segment_id: toIntegerOrNull(headerForm.segment_id),
      business_unit_id: hasBusinessUnitScope ? toIntegerOrNull(headerForm.business_unit_id) : null,
      rate_table_code: headerForm.rate_table_code.trim(),
      rate_table_name: headerForm.name.trim(),
      default_ceiling_pct: toNumberOrNull(headerForm.default_ceiling_pct),
      attainment_ceiling_pct: toNumberOrNull(headerForm.attainment_ceiling_pct),
      plan_component_id: toIntegerOrNull(headerForm.plan_component_id),
      effective_date: effectiveDate,
    };
  }, [hasBusinessUnitScope, headerForm, planComponents]);

  const buildRowPayload = useCallback((values) => {
    const fromPct = toNumberOrNull(values.from_pct);
    return {
      rate_table_id: toIntegerOrNull(selectedRateTableId),
      band_sequence: toIntegerOrNull(values.band_sequence),
      from_pct: fromPct,
      to_pct: toNumberOrNull(values.to_pct),
      payout_pct: toNumberOrNull(values.payout_pct),
      is_accelerator: Number.isFinite(fromPct) && fromPct >= 1.0,
      interpolation: values.interpolation || null
    };
  }, [selectedRateTableId]);

  // ── S471 Unified-path import handlers ────────────────────────────────────
  // POST /api/v1/import/upload — file_type=rate_table_shell | rate_table_row
  // Column mapping auto-seeded on first confirm (idempotent upsert).

  const ensureRateTableShellColumnMapping = useCallback(async () => {
    const mappings = [
      { source_column_name: 'rate_table_code',        target_column_name: 'rate_table_code',        is_required: true  },
      { source_column_name: 'rate_table_name',        target_column_name: 'rate_table_name',        is_required: true  },
      { source_column_name: 'plan_code',              target_column_name: 'plan_code',              is_required: true  },
      { source_column_name: 'role_code',              target_column_name: 'role_code',              is_required: true  },
      { source_column_name: 'segment_code',           target_column_name: 'segment_code',           is_required: true  },
      { source_column_name: 'default_ceiling_pct',    target_column_name: 'default_ceiling_pct',    is_required: true  },
      { source_column_name: 'attainment_ceiling_pct', target_column_name: 'attainment_ceiling_pct', is_required: true  },
      { source_column_name: 'effective_date',         target_column_name: 'effective_date',         is_required: true  },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'rate_table_shell', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed rate table shell column mapping.');
  }, []);

  const ensureRateTableRowColumnMapping = useCallback(async () => {
    const mappings = [
      { source_column_name: 'rate_table_code', target_column_name: 'rate_table_code', is_required: true  },
      { source_column_name: 'from_pct',        target_column_name: 'from_pct',        is_required: true  },
      { source_column_name: 'to_pct',          target_column_name: 'to_pct',          is_required: true  },
      { source_column_name: 'payout_pct',      target_column_name: 'payout_pct',      is_required: true  },
      { source_column_name: 'interpolation',   target_column_name: 'interpolation',   is_required: true  },
      { source_column_name: 'is_accelerator',  target_column_name: 'is_accelerator',  is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'rate_table_row', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed rate table row column mapping.');
  }, []);

  const SHELL_REQUIRED_COLS = [
    'rate_table_code', 'rate_table_name', 'plan_code',
    'role_code', 'segment_code', 'default_ceiling_pct',
    'attainment_ceiling_pct', 'effective_date',
  ];

  const ROW_REQUIRED_COLS = [
    'rate_table_code', 'from_pct', 'to_pct', 'payout_pct', 'interpolation',
  ];

  const runUnifiedValidate = useCallback(async (fileType, file) => {
    if (!file) return;
    setCsvUnifiedImporting(true);
    setCsvUnifiedError('');
    if (fileType === 'rate_table_shell') {
      setCsvShellsDryResult(null);
      setCsvShellsCommitted(false);
    } else {
      setCsvRowsDryResult(null);
      setCsvRowsCommitted(false);
    }
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvUnifiedError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const required = fileType === 'rate_table_shell' ? SHELL_REQUIRED_COLS : ROW_REQUIRED_COLS;
      const missing = required.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvUnifiedError(`Missing required columns: ${missing.join(', ')}`); return; }
      const result = { rows: lines.length - 1, warnings: [], errors: [] };
      if (fileType === 'rate_table_shell') {
        setCsvShellsDryResult(result);
      } else {
        setCsvRowsDryResult(result);
      }
    } catch { setCsvUnifiedError('Failed to parse CSV.'); }
    finally { setCsvUnifiedImporting(false); }
  }, []);

  const runUnifiedCommit = useCallback(async (fileType, file, dryResult) => {
    if (!file || !dryResult) return;
    setCsvUnifiedImporting(true);
    setCsvUnifiedError('');
    try {
      if (fileType === 'rate_table_shell') {
        await ensureRateTableShellColumnMapping();
      } else {
        await ensureRateTableRowColumnMapping();
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', fileType);
      const res = await fetch(`${API_BASE}/import/upload`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json();
      if (!res.ok) { setCsvUnifiedError(json?.message || json?.detail || `Error ${res.status}`); return; }
      if (fileType === 'rate_table_shell') {
        setCsvShellsCommitted(true);
        setCsvShellsDryResult(null);
        setCsvShellsFile(null);
        showToast(`${json.import_log?.rows_loaded ?? dryResult.rows} shell(s) created — approve before loading bands.`);
        await loadRateTables();
      } else {
        setCsvRowsCommitted(true);
        setCsvRowsDryResult(null);
        setCsvRowsFile(null);
        showToast(`${json.import_log?.rows_loaded ?? dryResult.rows} band row(s) loaded.`);
        await loadRateTables();
      }
    } catch (e) { setCsvUnifiedError(e.message || 'Commit failed.'); }
    finally { setCsvUnifiedImporting(false); }
  }, [ensureRateTableShellColumnMapping, ensureRateTableRowColumnMapping, loadRateTables, showToast]);

  const saveRateTable = useCallback(async (event) => {
    event.preventDefault();
    const errors = validateHeader();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setInlineError('');
    try {
      const created = await fetchJson(`${API_BASE}/rate-tables`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildHeaderPayload())
      });
      if (created) {
        setRateTables((previous) => [created, ...previous]);
        setSelectedRateTableId(created.rate_table_id);
      } else {
        await loadRateTables();
      }
      setHeaderForm(EMPTY_HEADER_FORM);
      showToast('Rate table saved.');
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to save rate table.'));
    } finally {
      setSaving(false);
    }
  }, [buildHeaderPayload, fetchJson, loadRateTables, showToast, validateHeader]);

  const saveRateTableRow = useCallback(async (event) => {
    event.preventDefault();
    if (!selectedRateTableId) { setInlineError('Select a rate table before adding rows.'); return; }

    const errors = validateRow(rowForm);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const candidateRows = [...rows, buildRowPayload(rowForm)];
    const bandError = validateBandContiguity(candidateRows);
    if (bandError) { setInlineError(bandError); return; }

    setSaving(true);
    setInlineError('');
    try {
      const result = await fetchJson(`${API_BASE}/rate-tables/${selectedRateTableId}/rows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: [buildRowPayload(rowForm)] })
      });
      // Response: { rows: [...], rows_written: N }
      const written = result?.rows || [];
      if (written.length > 0) {
        setRows((previous) => {
          const ids = new Set(previous.map(r => r.rate_table_row_id));
          const newRows = written.filter(r => !ids.has(r.rate_table_row_id));
          return [...previous, ...newRows];
        });
      }
      setRowForm(EMPTY_ROW_FORM);
      showToast('Rate table row saved.');
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to save rate table row.'));
    } finally {
      setSaving(false);
    }
  }, [buildRowPayload, fetchJson, loadRowsForTable, rowForm, rows, selectedRateTableId, showToast, validateRow]);

  const approveRateTable = useCallback(async (rateTableId) => {
    const bandError = validateBandContiguity(rows);
    if (bandError) { setInlineError(bandError); return; }

    setSaving(true);
    setInlineError('');
    try {
      const updated = await fetchJson(`${API_BASE}/rate-tables/${rateTableId}/approve`, { method: 'POST' });
      setRateTables((previous) =>
        previous.map((table) =>
          table.rate_table_id === rateTableId
            ? { ...table, ...(updated || {}), approved_by: updated?.approved_by || table.approved_by || 'approved' }
            : table
        )
      );
      showToast('Rate table approved.');
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to approve rate table.'));
    } finally {
      setSaving(false);
    }
  }, [fetchJson, rows, showToast]);

  // DELETE /rate-table-rows/{id} does not exist — use POST /rows to overwrite (upsert by band_sequence)
  // For now, local removal only — re-posting the same band_sequence will overwrite via ON CONFLICT
  const deleteRow = useCallback((rowId) => {
    setRows((previous) => previous.filter((row) => row.rate_table_row_id !== rowId));
    showToast('Row removed locally. Re-posting same band_sequence will overwrite.');
  }, [showToast]);

  const selectedRateTable = useMemo(
    () => rateTables.find((table) => String(table.rate_table_id) === String(selectedRateTableId)) || null,
    [rateTables, selectedRateTableId]
  );

  // Shared Tailwind class strings
  const thClass = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
  const tdClass = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
  const btnPrimary = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
  const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
  const inputClass = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none';
  const labelClass = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Rate Table Builder</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication is required to build rate tables.
        </div>
      </section>
    );
  }

  if (blockingError) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Rate Table Builder</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {blockingError}
        </div>
        <button type="button" className={btnSecondary} onClick={loadReferenceData}>Retry</button>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">SETUP-05</p>
          <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Rate Table Builder</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Create rate tables and payout bands. Gap and overlap validation runs before row save and approval.
          </p>
        </div>
      </header>

      {/* Toast */}
      {toast ? (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      {/* Non-blocking warning */}
      {nonBlockingWarning ? (
        <div role="status" className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
          {nonBlockingWarning}
        </div>
      ) : null}

      {/* Inline error */}
      {inlineError ? (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {inlineError}
        </div>
      ) : null}

      {/* ── CSV Bulk Import (unified path — POST /api/v1/import/upload) ──── */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-5 shadow-sm">
        <div>
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">CSV Bulk Import</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Import rate table shells and bands via the unified CSV import endpoint.
            Step A creates unapproved shells — approve each before loading bands. Step B loads bands into approved tables.
          </p>
        </div>

        {csvUnifiedError ? (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {csvUnifiedError}
          </div>
        ) : null}

        {/* Step A — Rate table shells */}
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] text-tok-xs font-bold text-white">A</span>
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              RateTableShells.csv — create unapproved shells
              {csvShellsCommitted ? <span className="ml-2 text-[rgb(var(--color-success))] font-normal">✓ Loaded</span> : null}
            </span>
          </div>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Required columns: <code>rate_table_code, rate_table_name, plan_code, role_code, segment_code, default_ceiling_pct, attainment_ceiling_pct, effective_date</code>
            <span className="block mt-1 text-amber-600">⚠ Shells are created unapproved — approve each via the table list below before loading bands.</span>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file" accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setCsvShellsFile(f);
                setCsvShellsDryResult(null);
                setCsvShellsCommitted(false);
                setCsvUnifiedError('');
              }}
            />
            <button
              type="button" className={btnSecondary}
              disabled={!csvShellsFile || csvUnifiedImporting}
              onClick={() => runUnifiedValidate('rate_table_shell', csvShellsFile)}
            >
              {csvUnifiedImporting && !csvShellsDryResult ? 'Validating…' : 'Validate'}
            </button>
          </div>
          {csvShellsDryResult ? (
            <div className="mt-1 flex flex-col gap-2">
              <div className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Rows parsed: <strong>{csvShellsDryResult.rows}</strong> · Shells to create: <strong>{csvShellsDryResult.rows}</strong>
              </div>
              {csvShellsDryResult.errors?.length > 0 ? (
                <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvShellsDryResult.errors.length} error(s) — fix CSV and re-upload.</p>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">
                    ✓ Validation passed — {csvShellsDryResult.rows} shell(s) ready to create.
                  </span>
                  <button
                    type="button" className={btnPrimary}
                    disabled={csvUnifiedImporting}
                    onClick={() => runUnifiedCommit('rate_table_shell', csvShellsFile, csvShellsDryResult)}
                  >
                    {csvUnifiedImporting ? 'Creating…' : 'Confirm and create'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Step B — Rate table rows (bands) */}
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] text-tok-xs font-bold text-white">B</span>
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              RateTableBands.csv — load payout bands into approved tables
              {csvRowsCommitted ? <span className="ml-2 text-[rgb(var(--color-success))] font-normal">✓ Loaded</span> : null}
            </span>
          </div>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Required columns: <code>rate_table_code, from_pct, to_pct, payout_pct, interpolation</code>
            <span className="block mt-1">Optional: <code>is_accelerator</code> — auto-set server-side when from_pct ≥ 1.0. All pct values as decimals (0.5000 not 50%). Rate table must be approved before bands can be loaded.</span>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file" accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setCsvRowsFile(f);
                setCsvRowsDryResult(null);
                setCsvRowsCommitted(false);
                setCsvUnifiedError('');
              }}
            />
            <button
              type="button" className={btnSecondary}
              disabled={!csvRowsFile || csvUnifiedImporting}
              onClick={() => runUnifiedValidate('rate_table_row', csvRowsFile)}
            >
              {csvUnifiedImporting && !csvRowsDryResult ? 'Validating…' : 'Validate'}
            </button>
          </div>
          {csvRowsDryResult ? (
            <div className="mt-1 flex flex-col gap-2">
              <div className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Rows parsed: <strong>{csvRowsDryResult.rows}</strong> · Bands to load: <strong>{csvRowsDryResult.rows}</strong>
              </div>
              {csvRowsDryResult.errors?.length > 0 ? (
                <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvRowsDryResult.errors.length} error(s) — fix CSV and re-upload.</p>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">
                    ✓ Validation passed — {csvRowsDryResult.rows} band(s) ready to load.
                  </span>
                  <button
                    type="button" className={btnPrimary}
                    disabled={csvUnifiedImporting}
                    onClick={() => runUnifiedCommit('rate_table_row', csvRowsFile, csvRowsDryResult)}
                  >
                    {csvUnifiedImporting ? 'Loading…' : 'Confirm and load'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Single-row form (exception path) ─────────────────────────────────── */}
      {/* Rate table header form */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Add single rate table <span className="text-tok-sm font-normal text-[rgb(var(--color-text-muted))]">(exception path — use CSV import above for bulk loads)</span></h3>

        <form onSubmit={saveRateTable}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={labelClass}>
              Role
              <select className={inputClass} value={headerForm.role_id} onChange={(e) => updateHeaderForm('role_id', e.target.value)} disabled={referenceLoading}>
                <option value="">Select role</option>
                {roles.map((role) => (
                  <option key={role.role_id} value={role.role_id}>{role.role_name || role.name}</option>
                ))}
              </select>
              {validationErrors.role_id ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.role_id}</small> : null}
            </label>

            <label className={labelClass}>
              Segment
              <select className={inputClass} value={headerForm.segment_id} onChange={(e) => updateHeaderForm('segment_id', e.target.value)} disabled={referenceLoading}>
                <option value="">Select segment</option>
                {segments.map((segment) => (
                  <option key={segment.segment_id} value={segment.segment_id}>{segment.segment_name || segment.name}</option>
                ))}
              </select>
              {validationErrors.segment_id ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.segment_id}</small> : null}
            </label>

            {hasBusinessUnitScope ? (
              <label className={labelClass}>
                Business unit
                <select className={inputClass} value={headerForm.business_unit_id} onChange={(e) => updateHeaderForm('business_unit_id', e.target.value)}>
                  <option value="">Global</option>
                  {businessUnits.map((bu) => (
                    <option key={bu.business_unit_id} value={bu.business_unit_id}>{bu.business_unit_name || bu.name}</option>
                  ))}
                </select>
              </label>
            ) : null}

            <label className={labelClass}>
              Plan component
              <select className={inputClass} value={headerForm.plan_component_id} onChange={(e) => updateHeaderForm('plan_component_id', e.target.value)}>
                <option value="">Select plan component</option>
                {planComponents.map((component) => (
                  <option key={component.plan_component_id} value={component.plan_component_id}>
                    Component {component.plan_component_id}{component.approved_by ? ' · approved' : ' · unapproved'}
                  </option>
                ))}
              </select>
              {validationErrors.plan_component_id ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.plan_component_id}</small> : null}
            </label>

            <label className={labelClass}>
              Rate table code
              <input type="text" className={inputClass} value={headerForm.rate_table_code} onChange={(e) => updateHeaderForm('rate_table_code', e.target.value)} />
              {validationErrors.rate_table_code ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.rate_table_code}</small> : null}
            </label>

            <label className={labelClass}>
              Name
              <input type="text" className={inputClass} value={headerForm.name} onChange={(e) => updateHeaderForm('name', e.target.value)} />
              {validationErrors.name ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.name}</small> : null}
            </label>

            <label className={labelClass}>
              Default ceiling pct
              <input type="number" step="0.0001" className={inputClass} value={headerForm.default_ceiling_pct} onChange={(e) => updateHeaderForm('default_ceiling_pct', e.target.value)} />
              {validationErrors.default_ceiling_pct ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.default_ceiling_pct}</small> : null}
            </label>

            <label className={labelClass}>
              Attainment ceiling pct
              <input type="number" step="0.0001" className={inputClass} value={headerForm.attainment_ceiling_pct} onChange={(e) => updateHeaderForm('attainment_ceiling_pct', e.target.value)} />
              {validationErrors.attainment_ceiling_pct ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.attainment_ceiling_pct}</small> : null}
            </label>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button type="submit" className={btnPrimary} disabled={saving}>
              {saving ? 'Saving…' : 'Save rate table'}
            </button>
          </div>
        </form>
      </div>

      {/* Rate tables list */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Rate tables</h3>

        {rateTables.length === 0 && !loading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No rate tables have been created.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-tok-sm border-collapse">
              <thead>
                <tr>
                  <th className={thClass}>Code</th>
                  <th className={thClass}>Name</th>
                  <th className={thClass}>Plan component</th>
                  <th className={thClass}>Status</th>
                  <th className={thClass}>Engine eligibility</th>
                  <th className={thClass} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rateTables.map((table) => (
                  <tr key={table.rate_table_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className={tdClass}>{table.rate_table_code}</td>
                    <td className={tdClass}>{table.rate_table_name || table.name}</td>
                    <td className={tdClass}>{table.plan_component_id}</td>
                    <td className={tdClass}>{table.approved_by ? 'Approved' : 'Draft'}</td>
                    <td className={tdClass}>{table.approved_by ? 'Engine-eligible' : 'Engine-excluded'}</td>
                    <td className={tdClass}>
                      <div className="flex items-center gap-1">
                        <button type="button" className={btnSecondary} onClick={() => setSelectedRateTableId(table.rate_table_id)}>
                          Rows
                        </button>
                        <button type="button" className={btnSecondary} onClick={() => approveRateTable(table.rate_table_id)} disabled={saving || Boolean(table.approved_by)}>
                          Approve
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button type="button" className={btnSecondary} onClick={loadRateTables} disabled={loading}>
            Refresh rate tables
          </button>
        </div>
      </div>

      {/* Band row editor for selected table */}
      {selectedRateTable ? (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Rows for {selectedRateTable.rate_table_code}</h3>

          <form onSubmit={saveRateTableRow}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className={labelClass}>
                Band sequence
                <input type="number" step="1" className={inputClass} value={rowForm.band_sequence} onChange={(e) => updateRowForm('band_sequence', e.target.value)} />
                {validationErrors.band_sequence ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.band_sequence}</small> : null}
              </label>

              <label className={labelClass}>
                from_pct
                <input type="number" step="0.0001" className={inputClass} value={rowForm.from_pct} onChange={(e) => updateRowForm('from_pct', e.target.value)} />
                {validationErrors.from_pct ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.from_pct}</small> : null}
              </label>

              <label className={labelClass}>
                to_pct
                <input type="number" step="0.0001" className={inputClass} value={rowForm.to_pct} onChange={(e) => updateRowForm('to_pct', e.target.value)} />
                {validationErrors.to_pct ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.to_pct}</small> : null}
              </label>

              <label className={labelClass}>
                payout_pct
                <input type="number" step="0.0001" className={inputClass} value={rowForm.payout_pct} onChange={(e) => updateRowForm('payout_pct', e.target.value)} />
                {validationErrors.payout_pct ? <small className="text-tok-xs text-[rgb(var(--color-danger))] mt-1">{validationErrors.payout_pct}</small> : null}
              </label>

              <label className={labelClass}>
                interpolation
                {/* DDL CHECK: interpolation IN ('linear', 'step') — free text not permitted */}
                <select className={inputClass} value={rowForm.interpolation} onChange={(e) => updateRowForm('interpolation', e.target.value)}>
                  <option value="linear">linear</option>
                  <option value="step">step</option>
                </select>
              </label>

              <label className={labelClass}>
                Accelerator
                <input type="checkbox" className="mt-2 h-4 w-4" checked={rowForm.is_accelerator} readOnly />
              </label>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <button type="submit" className={btnPrimary} disabled={saving}>
                {saving ? 'Saving…' : 'Add row'}
              </button>
            </div>
          </form>

          {rows.length === 0 ? (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No bands have been created for this table.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Sequence</th>
                    <th className={thClass}>from_pct</th>
                    <th className={thClass}>to_pct</th>
                    <th className={thClass}>payout_pct</th>
                    <th className={thClass}>Accelerator</th>
                    <th className={thClass}>Interpolation</th>
                    <th className={thClass} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {[...rows]
                    .sort((left, right) => Number(left.band_sequence) - Number(right.band_sequence))
                    .map((row) => (
                      <tr key={row.rate_table_row_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className={tdClass}>{row.band_sequence}</td>
                        <td className={tdClass}>{row.from_pct}</td>
                        <td className={tdClass}>{row.to_pct}</td>
                        <td className={tdClass}>{row.payout_pct}</td>
                        <td className={tdClass}>{row.is_accelerator ? 'Yes' : 'No'}</td>
                        <td className={tdClass}>{row.interpolation || '—'}</td>
                        <td className={tdClass}>
                          <button type="button" className={btnSecondary} onClick={() => deleteRow(row.rate_table_row_id)} disabled={saving}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* Footer nav */}
      <footer className="flex items-center gap-3 pt-4 border-t border-[rgb(var(--color-border))]">
        <button type="button" className={btnSecondary} onClick={onBack}>Back</button>
        <button type="button" className={btnPrimary} onClick={onNext}>Continue to assignments</button>
      </footer>
    </section>
  );
}

export default RateTableBuilder;
